import React, { useState } from "react";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { AssessmentRecord } from "./types";
import ReportConfirmationDialog from "./ReportConfirmationDialog";

interface AssessmentCardProps {
    record: AssessmentRecord;
    onClick: (record: AssessmentRecord) => void;
    onGenerateReport?: (record: AssessmentRecord) => void;
}

export const AssessmentCard: React.FC<AssessmentCardProps> = ({
    record,
    onClick,
    onGenerateReport,
}) => {
    const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);

    const handleClick = (e: React.MouseEvent) => {
        // If clicking the generate report button, don't trigger the card click
        if ((e.target as HTMLElement).closest("button")) {
            e.stopPropagation();
            return;
        }
        onClick(record);
    };

    const handleGenerateReport = () => {
        setIsReportDialogOpen(true);
    };

    const handleConfirmReport = () => {
        setIsReportDialogOpen(false);
        onGenerateReport?.(record);
    };

    const isGeneratingReport =
        record.reportStatus?.toLowerCase() === "processing" ||
        record.reportStatus?.toLowerCase() === "queued";
    console.log("Is generating report", isGeneratingReport);
    return (
        <>
            <Card
                className="p-6 cursor-pointer hover:shadow-md transition-shadow"
                onClick={handleClick}>
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex-1 mr-2">
                        {record.title}
                    </h3>
                    <Badge
                        variant={
                            record.status === "Completed"
                                ? "secondary"
                                : "default"
                        }
                        className="whitespace-nowrap">
                        {record.status}
                    </Badge>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <span className="text-gray-600">Date</span>
                        <span className="font-medium text-gray-900">
                            {record.date}
                        </span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-600">Duration</span>
                        <span className="font-medium text-gray-900">
                            {record.duration}
                        </span>
                    </div>
                    {/* Score display commented out */}
                    {/* {record.score !== undefined && (
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">Score</span>
                            <span className="font-medium text-gray-900">
                                {record.score}
                            </span>
                        </div>
                    )} */}

                    {record.status === "Attempted" && (
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">Get Report</span>
                            {isGeneratingReport ? (
                                <span className="text-sm text-gray-500 italic">
                                    Generating...
                                </span>
                            ) : (
                                onGenerateReport && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleGenerateReport}
                                        className="flex items-center gap-2 text-primary hover:text-primary hover:bg-primary/10">
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line
                                                x1="12"
                                                y1="15"
                                                x2="12"
                                                y2="3"
                                            />
                                        </svg>
                                        Generate Report
                                    </Button>
                                )
                            )}
                        </div>
                    )}
                    {record.status !== "Attempted" && isGeneratingReport && (
                        <div className="flex justify-between items-center">
                            <span className="text-gray-600">Get Report</span>
                            <span className="text-sm text-gray-500 italic">
                                Generating...
                            </span>
                        </div>
                    )}
                </div>
            </Card>
            <ReportConfirmationDialog
                isOpen={isReportDialogOpen}
                onClose={() => setIsReportDialogOpen(false)}
                onConfirm={handleConfirmReport}
            />
        </>
    );
};
